import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Wrench, Shield, Building2 } from "lucide-react";
import PinPad from "../components/PinPad";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [step, setStep] = useState("select"); // select | pin
  const [target, setTarget] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, session } = useAuth();

  useEffect(() => {
    if (session) {
      navigate(session.tipo === "master" ? "/master" : `/unidade/${session.unidade.slug}`);
    }
    api.get("/unidades").then((r) => setUnidades(r.data)).catch(() => {});
  }, [session, navigate]);

  const openPin = (t) => {
    setTarget(t);
    setPin("");
    setStep("pin");
  };

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (target.tipo === "master") {
        const res = await api.post("/auth/login", { tipo: "master", senha: pin });
        login({ tipo: "master", nome: res.data.nome });
        toast.success("Bem-vindo, Master");
        navigate("/master");
      } else {
        const res = await api.post("/auth/login", {
          tipo: "unidade",
          senha: pin,
          unidade_id: target.unidade.id,
        });
        login({ tipo: "unidade", unidade: res.data.unidade });
        toast.success(`Bem-vindo, ${res.data.unidade.nome}`);
        navigate(`/unidade/${res.data.unidade.slug}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao entrar");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const santaClara = unidades.find((u) => u.slug === "santa_clara");
  const lacador = unidades.find((u) => u.slug === "lacador");

  const tiles = [
    {
      key: "sc",
      onClick: () => santaClara && openPin({ tipo: "unidade", unidade: santaClara }),
      bg: "bg-blue-600",
      hover: "hover:bg-blue-700",
      label: "Posto Santa Clara",
      sub: "Unidade · Azul",
      icon: Building2,
      testid: "login-santaclara-btn",
    },
    {
      key: "lc",
      onClick: () => lacador && openPin({ tipo: "unidade", unidade: lacador }),
      bg: "bg-red-600",
      hover: "hover:bg-red-700",
      label: "Posto Laçador",
      sub: "Unidade · Vermelho",
      icon: Wrench,
      testid: "login-lacador-btn",
    },
    {
      key: "master",
      onClick: () => openPin({ tipo: "master" }),
      bg: "bg-neutral-950",
      hover: "hover:bg-neutral-800",
      label: "Painel Master",
      sub: "Administração global",
      icon: Shield,
      testid: "login-master-btn",
    },
  ];

  return (
    <div
      className="min-h-screen relative bg-neutral-950"
      style={{
        backgroundImage: `
          radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.18) 0%, transparent 45%),
          radial-gradient(circle at 80% 70%, rgba(220, 38, 38, 0.18) 0%, transparent 45%),
          radial-gradient(circle at 50% 50%, rgba(234, 179, 8, 0.05) 0%, transparent 60%),
          linear-gradient(to bottom, #0a0a0a 0%, #171717 100%)
        `,
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 40px)",
        }}
      />
      <div className="absolute inset-0 bg-neutral-950/40" />
      <div className="relative min-h-screen flex flex-col">
        {/* Header strip */}
        <header className="border-b-4 border-yellow-400 bg-neutral-950 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                Sistema de Gestão
              </p>
              <h1 className="font-display font-black text-white text-lg tracking-tight">
                OLEO · OPS
              </h1>
            </div>
          </div>
          <div className="hidden sm:flex font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
            v1.0 · Troca de Óleo
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-5xl">
            {step === "select" && (
              <div>
                <div className="text-center mb-10">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
                    Acesso restrito
                  </p>
                  <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter">
                    Selecione o Terminal
                  </h2>
                  <p className="mt-4 text-neutral-300 max-w-xl mx-auto">
                    Cada unidade tem identidade visual própria. Garanta que escolheu a correta.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  {tiles.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.key}
                        data-testid={t.testid}
                        onClick={t.onClick}
                        className={`group ${t.bg} ${t.hover} text-white text-left p-8 border-4 border-white/10 cta-brutal transition-all`}
                      >
                        <Icon className="w-10 h-10 mb-6" strokeWidth={2} />
                        <p className="font-mono text-xs uppercase tracking-[0.25em] opacity-70 mb-2">
                          {t.sub}
                        </p>
                        <h3 className="font-display text-3xl font-black tracking-tighter">
                          {t.label}
                        </h3>
                        <div className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                          <Lock className="w-3.5 h-3.5" />
                          Requer PIN
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === "pin" && target && (
              <div className="mx-auto max-w-md">
                <div className="bg-white border-4 border-neutral-900 shadow-brutal p-6 sm:p-8">
                  <button
                    data-testid="pin-back-btn"
                    onClick={() => setStep("select")}
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-neutral-900 mb-6"
                  >
                    ← Voltar
                  </button>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    {target.tipo === "master" ? "Painel Master" : target.unidade.nome}
                  </p>
                  <h2 className="font-display text-3xl font-black tracking-tighter text-neutral-950 mb-6">
                    Digite o PIN
                  </h2>

                  <div
                    data-testid="pin-display"
                    className="border-2 border-neutral-900 p-4 mb-6 font-mono text-3xl tracking-[0.5em] text-center min-h-[60px]"
                  >
                    {pin.replace(/./g, "●") || (
                      <span className="text-neutral-300 text-xl tracking-widest">
                        ----
                      </span>
                    )}
                  </div>

                  <PinPad value={pin} onChange={setPin} onSubmit={submit} />
                </div>
              </div>
            )}
          </div>
        </main>

        <footer className="border-t border-neutral-800 py-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          Santa Clara · Laçador · Painel Master
        </footer>
      </div>
    </div>
  );
}
